import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, FileText, AlertCircle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const StudentLedger = () => {
  const [hostelStudents, setHostelStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [student, setStudent] = useState(null);
  const [payments, setPayments] = useState([]);
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dueMonths, setDueMonths] = useState([]);

  useEffect(() => {
    fetchHostelStudents();
  }, []);

  const fetchHostelStudents = async () => {
    try {
      const response = await apiClient.get('/api/hostel/students');
      setHostelStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchLedger = async (studentId) => {
    if (!studentId) return;
    
    setLoading(true);
    try {
      const selectedStudent = hostelStudents.find(s => s.id === studentId);
      if (!selectedStudent) {
        alert('Student not found');
        return;
      }
      
      setStudent(selectedStudent);
      
      // Fetch payments for this student
      const paymentsRes = await apiClient.get('/api/hostel/fee-payments');
      const allPayments = Array.isArray(paymentsRes.data) ? paymentsRes.data : [];
      const studentPayments = allPayments.filter(p => 
        p.student_id === studentId || 
        p.payer_name === selectedStudent.student_name
      );
      setPayments(studentPayments);
      
      // Fetch fines for this student
      const finesRes = await apiClient.get('/api/hostel/fines');
      const allFines = Array.isArray(finesRes.data) ? finesRes.data : [];
      const studentFines = allFines.filter(f => 
        f.student_id === studentId || 
        f.student_name === selectedStudent.student_name
      );
      setFines(studentFines);
      
      // Calculate due months
      calculateDueMonths(selectedStudent, studentPayments);
      
    } catch (error) {
      console.error('Error:', error);
      alert('Error fetching ledger');
    } finally {
      setLoading(false);
    }
  };

  const calculateDueMonths = (studentData, studentPayments) => {
    const admissionDate = new Date(studentData.admission_date);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    const paidMonths = studentPayments.map(p => `${p.month}-${p.year}`);
    
    const dues = [];
    let startYear = admissionDate.getFullYear();
    let startMonth = admissionDate.getMonth();
    
    for (let year = startYear; year <= currentYear; year++) {
      const monthStart = (year === startYear) ? startMonth : 0;
      const monthEnd = (year === currentYear) ? currentMonth : 11;
      
      for (let month = monthStart; month <= monthEnd; month++) {
        const monthName = MONTHS[month];
        const key = `${monthName}-${year}`;
        
        if (!paidMonths.includes(key)) {
          dues.push({ month: monthName, year, key });
        }
      }
    }
    
    setDueMonths(dues);
  };

  const handleStudentChange = (studentId) => {
    setSelectedStudentId(studentId);
    fetchLedger(studentId);
  };

  // Calculate summaries
  const totalPaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalFines = fines.reduce((sum, f) => sum + (f.fine_amount || 0), 0);
  const totalFinePaid = fines.reduce((sum, f) => sum + (f.paid_amount || 0), 0);
  const pendingFines = totalFines - totalFinePaid;
  const totalDue = dueMonths.length * (student?.monthly_fee || 0);
  const grandTotal = totalDue + pendingFines;

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Student Hostel Ledger
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Select Hostel Student</Label>
              <Select value={selectedStudentId} onValueChange={handleStudentChange}>
                <SelectTrigger>
                  <SelectValue placeholder="-- Select Student --" />
                </SelectTrigger>
                <SelectContent>
                  {hostelStudents.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.student_name} ({s.hostel_name} - Room {s.room_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {student && (
        <>
          {/* Student Info */}
          <Card>
            <CardHeader><CardTitle>Student Information</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><strong>Name:</strong> {student.student_name}</div>
                <div><strong>Student ID:</strong> {student.student_id}</div>
                <div><strong>Hostel:</strong> {student.hostel_name}</div>
                <div><strong>Room:</strong> {student.room_number}</div>
                <div><strong>Monthly Fee:</strong> <span className="font-bold text-blue-600">₹{student.monthly_fee}</span></div>
                <div><strong>Admission Date:</strong> {formatDate(student.admission_date)}</div>
                <div><strong>Status:</strong> 
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${student.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {student.status}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-5 gap-4">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="text-sm text-green-600">Total Fee Paid</div>
                <div className="text-2xl font-bold text-green-700">₹{totalPaid}</div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="text-sm text-red-600">Fee Due ({dueMonths.length} months)</div>
                <div className="text-2xl font-bold text-red-700">₹{totalDue}</div>
              </CardContent>
            </Card>
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-4">
                <div className="text-sm text-orange-600">Total Fines</div>
                <div className="text-2xl font-bold text-orange-700">₹{totalFines}</div>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4">
                <div className="text-sm text-yellow-600">Pending Fines</div>
                <div className="text-2xl font-bold text-yellow-700">₹{pendingFines}</div>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <div className="text-sm text-purple-600">Grand Total Due</div>
                <div className="text-2xl font-bold text-purple-700">₹{grandTotal}</div>
              </CardContent>
            </Card>
          </div>

          {/* Due Months Alert */}
          {dueMonths.length > 0 && (
            <Card className="border-red-300 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-red-700 mb-2">
                  <AlertCircle className="w-5 h-5" />
                  <strong>Due Months ({dueMonths.length}):</strong>
                </div>
                <div className="flex flex-wrap gap-2">
                  {dueMonths.map(d => (
                    <span key={d.key} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                      {d.month} {d.year}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment History */}
          <Card>
            <CardHeader><CardTitle>Fee Payment History</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Receipt #</th>
                    <th className="text-left p-2">Month/Year</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-left p-2">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr><td colSpan="5" className="text-center p-4 text-gray-500">No payments found.</td></tr>
                  ) : (
                    payments.map((payment) => (
                      <tr key={payment.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">{formatDate(payment.payment_date)}</td>
                        <td className="p-2">{payment.receipt_number || payment.payment_number}</td>
                        <td className="p-2">{payment.month} {payment.year}</td>
                        <td className="p-2 text-right text-green-600 font-medium">₹{payment.amount}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 text-xs rounded ${payment.payment_mode === 'Cash' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                            {payment.payment_mode}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Fines History */}
          <Card>
            <CardHeader><CardTitle>Fines History</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Fine #</th>
                    <th className="text-left p-2">Reason</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-right p-2">Paid</th>
                    <th className="text-left p-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {fines.length === 0 ? (
                    <tr><td colSpan="6" className="text-center p-4 text-gray-500">No fines found.</td></tr>
                  ) : (
                    fines.map((fine) => (
                      <tr key={fine.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">{formatDate(fine.fine_date)}</td>
                        <td className="p-2">{fine.fine_number}</td>
                        <td className="p-2">{fine.fine_reason}</td>
                        <td className="p-2 text-right">₹{fine.fine_amount}</td>
                        <td className="p-2 text-right text-green-600">₹{fine.paid_amount || 0}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${fine.payment_status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {fine.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default StudentLedger;
